import React, { createContext, useContext, useReducer, useCallback } from "react"

const VisualEditorContext = createContext()

const initialState = {
  productId: null,
  productName: "",
  steps: [],
  types: [],
  materialsList: [],
  activeTab: 0,
  isDirty: false,
  selectedStep: null,
  isLoading: false,
  isSaving: false,
  materialsDrawerOpen: false,
}

const actionTypes = {
  SET_PRODUCT_DATA: "SET_PRODUCT_DATA",
  SET_STEPS: "SET_STEPS",
  ADD_STEP: "ADD_STEP",
  DELETE_STEP: "DELETE_STEP",
  MOVE_STEP: "MOVE_STEP",
  UPDATE_STEP: "UPDATE_STEP",
  ADD_MATERIAL_TO_STEP: "ADD_MATERIAL_TO_STEP",
  REMOVE_MATERIAL_FROM_STEP: "REMOVE_MATERIAL_FROM_STEP",
  UPDATE_MATERIAL_QUANTITY: "UPDATE_MATERIAL_QUANTITY",
  SET_ACTIVE_TAB: "SET_ACTIVE_TAB",
  SET_SELECTED_STEP: "SET_SELECTED_STEP",
  SET_MATERIALS_DRAWER: "SET_MATERIALS_DRAWER",
  SET_LOADING: "SET_LOADING",
  SET_SAVING: "SET_SAVING",
  SET_DIRTY: "SET_DIRTY",
  RESET: "RESET",
}

function reducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_PRODUCT_DATA:
      return {
        ...state,
        productId: action.payload.productId,
        productName: action.payload.productName,
        steps: action.payload.steps || [],
        types: action.payload.types || [],
        materialsList: action.payload.materialsList || [],
        isDirty: false,
      }

    case actionTypes.SET_STEPS:
      return {
        ...state,
        steps: action.payload,
        isDirty: true,
      }

    case actionTypes.ADD_STEP: {
      const newStep = {
        seq: state.steps.length + 1,
        typid: action.payload.typid,
        materials: [],
        files: [],
        instructions: "",
      }
      return {
        ...state,
        steps: [...state.steps, newStep],
        isDirty: true,
      }
    }

    case actionTypes.DELETE_STEP: {
      const newSteps = state.steps
        .filter((_, index) => index !== action.payload)
        .map((step, index) => ({ ...step, seq: index + 1 }))
      return {
        ...state,
        steps: newSteps,
        isDirty: true,
      }
    }

    case actionTypes.MOVE_STEP: {
      const { fromIndex, toIndex } = action.payload
      const newSteps = [...state.steps]
      const [removed] = newSteps.splice(fromIndex, 1)
      newSteps.splice(toIndex, 0, removed)
      // Update sequence numbers
      const updatedSteps = newSteps.map((step, index) => ({
        ...step,
        seq: index + 1,
      }))
      return {
        ...state,
        steps: updatedSteps,
        isDirty: true,
      }
    }

    case actionTypes.UPDATE_STEP: {
      const { stepIndex, updates } = action.payload
      const newSteps = [...state.steps]
      newSteps[stepIndex] = { ...newSteps[stepIndex], ...updates }
      return {
        ...state,
        steps: newSteps,
        isDirty: true,
      }
    }

    case actionTypes.ADD_MATERIAL_TO_STEP: {
      const { stepIndex, material } = action.payload
      const newSteps = [...state.steps]
      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        materials: [...(newSteps[stepIndex].materials || []), { ...material, quantity: 0 }],
      }
      return {
        ...state,
        steps: newSteps,
        isDirty: true,
      }
    }

    case actionTypes.REMOVE_MATERIAL_FROM_STEP: {
      const { stepIndex, materialIndex } = action.payload
      const newSteps = [...state.steps]
      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        materials: newSteps[stepIndex].materials.filter((_, i) => i !== materialIndex),
      }
      return {
        ...state,
        steps: newSteps,
        isDirty: true,
      }
    }

    case actionTypes.UPDATE_MATERIAL_QUANTITY: {
      const { stepIndex, materialIndex, quantity } = action.payload
      const newSteps = [...state.steps]
      newSteps[stepIndex].materials[materialIndex] = {
        ...newSteps[stepIndex].materials[materialIndex],
        quantity: quantity.replace(",", "."),
      }
      return {
        ...state,
        steps: newSteps,
        isDirty: true,
      }
    }

    case actionTypes.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload }

    case actionTypes.SET_SELECTED_STEP:
      return { ...state, selectedStep: action.payload }

    case actionTypes.SET_MATERIALS_DRAWER:
      return { ...state, materialsDrawerOpen: action.payload }

    case actionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload }

    case actionTypes.SET_SAVING:
      return { ...state, isSaving: action.payload }

    case actionTypes.SET_DIRTY:
      return { ...state, isDirty: action.payload }

    case actionTypes.RESET:
      return initialState

    default:
      return state
  }
}

export function VisualEditorProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setProductData = useCallback((data) => {
    dispatch({ type: actionTypes.SET_PRODUCT_DATA, payload: data })
  }, [])

  const setSteps = useCallback((steps) => {
    dispatch({ type: actionTypes.SET_STEPS, payload: steps })
  }, [])

  const addStep = useCallback((typid) => {
    dispatch({ type: actionTypes.ADD_STEP, payload: { typid } })
  }, [])

  const deleteStep = useCallback((stepIndex) => {
    dispatch({ type: actionTypes.DELETE_STEP, payload: stepIndex })
  }, [])

  const moveStep = useCallback((fromIndex, toIndex) => {
    dispatch({ type: actionTypes.MOVE_STEP, payload: { fromIndex, toIndex } })
  }, [])

  const updateStep = useCallback((stepIndex, updates) => {
    dispatch({ type: actionTypes.UPDATE_STEP, payload: { stepIndex, updates } })
  }, [])

  const addMaterialToStep = useCallback((stepIndex, material) => {
    dispatch({ type: actionTypes.ADD_MATERIAL_TO_STEP, payload: { stepIndex, material } })
  }, [])

  const removeMaterialFromStep = useCallback((stepIndex, materialIndex) => {
    dispatch({ type: actionTypes.REMOVE_MATERIAL_FROM_STEP, payload: { stepIndex, materialIndex } })
  }, [])

  const updateMaterialQuantity = useCallback((stepIndex, materialIndex, quantity) => {
    dispatch({ type: actionTypes.UPDATE_MATERIAL_QUANTITY, payload: { stepIndex, materialIndex, quantity } })
  }, [])

  const setActiveTab = useCallback((tab) => {
    dispatch({ type: actionTypes.SET_ACTIVE_TAB, payload: tab })
  }, [])

  const setSelectedStep = useCallback((stepIndex) => {
    dispatch({ type: actionTypes.SET_SELECTED_STEP, payload: stepIndex })
  }, [])

  const setMaterialsDrawerOpen = useCallback((open) => {
    dispatch({ type: actionTypes.SET_MATERIALS_DRAWER, payload: open })
  }, [])

  const setLoading = useCallback((loading) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: loading })
  }, [])

  const setSaving = useCallback((saving) => {
    dispatch({ type: actionTypes.SET_SAVING, payload: saving })
  }, [])

  const setDirty = useCallback((dirty) => {
    dispatch({ type: actionTypes.SET_DIRTY, payload: dirty })
  }, [])

  const reset = useCallback(() => {
    dispatch({ type: actionTypes.RESET })
  }, [])

  const value = {
    ...state,
    setProductData,
    setSteps,
    addStep,
    deleteStep,
    moveStep,
    updateStep,
    addMaterialToStep,
    removeMaterialFromStep,
    updateMaterialQuantity,
    setActiveTab,
    setSelectedStep,
    setMaterialsDrawerOpen,
    setLoading,
    setSaving,
    setDirty,
    reset,
  }

  return (
    <VisualEditorContext.Provider value={value}>
      {children}
    </VisualEditorContext.Provider>
  )
}

export function useVisualEditor() {
  const context = useContext(VisualEditorContext)
  if (!context) {
    throw new Error("useVisualEditor must be used within a VisualEditorProvider")
  }
  return context
}

export const ItemTypes = {
  SECTION: "section",
  STEP: "step",
  MATERIAL: "material",
}

export default VisualEditorContext
